import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "../../../../Redux/Actions";
import { Award } from "../Awards/rnr.types";
import ModalComponents from "../../../../component/ModalComponent/ModalComponent";
import RewardsListDropdown from "./NominationRewardsListDropdown";
import { Form } from "react-bootstrap";
import { showSuccessToastMessage, showErrorToastMessage } from "../../../../component/Toasts/Toasts";
import "./ReportingManagerDetails.scss";
import Button from "../../../../component/Buttons/Button";

interface NominationModalProps {
    showModal: boolean;
    onHide: () => void; 
    employee: any;
    unavailableAwardIds: string[];
    allAwards: Award[]; 
}

// interface Award {
//     _id: string;
//     awardCategory: string;
//     frequency: string;
//     definition: string;
//     approvalWorkflow?: string;
//     result?: string[];
//     remarks?: string;
//     eligibility?: Record<string, any>;
//     notificationRules?: Record<string, any>;
// }

export default function NominationModal({ showModal, onHide, employee, unavailableAwardIds, allAwards }: NominationModalProps) {
    const dispatch = useDispatch();
    const [selectedAward, setSelectedAward] = useState<Award | null>(null);
    const [justification, setJustification] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const loginDetailsString = localStorage.getItem("login");
    const loginDetailsObject = JSON.parse(loginDetailsString ?? "{}"); ;
    const nominatorId = loginDetailsObject.id;
    console.log(nominatorId); 

    const handleAwardSelect = (award: Award) => {
        const today = new Date();
        if (
            award.nominationWindow?.startDate &&
            award.nominationWindow?.endDate
        ) {
            const start = new Date(award.nominationWindow.startDate);
            const end = new Date(award.nominationWindow.endDate);

            if (today < start || today > end) {
            showErrorToastMessage("This award's nomination window is currently closed.");
            return;
            }
        }

        setSelectedAward(award);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); 
        event.stopPropagation();
        
        if (!selectedAward) {
            showErrorToastMessage("Please select an award.");
            return;
        }
        if (!justification.trim()) {
            showErrorToastMessage("Please provide a justification for the nomination.");
            return;
        }
        if (!nominatorId) {
            showErrorToastMessage("Could not identify the nominator. Please log in again.");
            return;
        }

        setIsSubmitting(true);

        const nominationData = {
            awardRef: selectedAward._id,      
            employeeRef: employee._id,       
            nominatedByRef: nominatorId,     
            justification: justification,
            status: 'Pending',
        };

        dispatch(actions.submitNominationAction(nominationData, (res: any) => {
            setIsSubmitting(false);
            if (res.success) {
                showSuccessToastMessage("Nomination submitted successfully!");
                onHide();
            } else {
                showErrorToastMessage(res.error?.error_description || "Failed to submit nomination.");
            }
        }));
    };

    const convertExperienceToMonths = (expString: string | null | undefined): number => {
        if (!expString) return 0;
        const parts = String(expString).split('.');
        const years = parseInt(parts[0], 10) || 0;
        const months = parts.length > 1 ? parseInt(parts[1], 10) || 0 : 0;
        return (years * 12) + months;
    };

    // 4. Use useMemo to calculate an enriched list of awards with eligibility status
    const awardsWithEligibility = useMemo(() => {
        if (!allAwards || !employee) return [];

        const employeeMonths = convertExperienceToMonths(employee.organisationExperience);

        return allAwards.map(award => {
            let isEligible = true;
            const requiredTenure = award.eligibility?.tenure;

            // Tenure Check
            if (requiredTenure && employeeMonths < requiredTenure) {
                isEligible = false;
            }
            
            // NOTE: Add other eligibility checks here in the future if needed
            // Example for Employment Type:
            // const requiredEmpTypes = award.eligibility?.employmentType;
            // if (isEligible && requiredEmpTypes && requiredEmpTypes.length > 0) {
            //     if (!employee.employmentType || !requiredEmpTypes.includes(employee.employmentType)) {
            //         isEligible = false;
            //     }
            // }

            return { ...award, isEligible };
        });
    }, [allAwards, employee]);
    
    const modalBodyContent = (
        <div className="container-fluid"> 
            {/* Employee Details */}
            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label"><b>Employee ID</b></label>
                    <input 
                        type="text"
                        disabled
                        className="form-control"
                        value={employee?.employeeId || 'N/A'}
                    />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label"><b>Employee Name</b></label>
                    <input 
                        type="text"
                        disabled
                        className="form-control"
                        value={`${employee?.candidateName || ''} ${employee?.middleName || ''} ${employee?.lastname || ''}`.trim()}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-md-12 mb-3">
                    <label className="form-label"><b>Employee Designation</b></label>
                    <input 
                        type="text"
                        disabled
                        className="form-control"
                        value={employee?.designation || 'N/A'}
                    />
                </div>

                <div className="col-md-12 mb-3">
                    <label className="form-label"><b>Employee Department</b></label>
                    <input 
                        type="text"
                        disabled
                        className="form-control"
                        value={employee?.department || 'N/A'}
                    />
                </div>
            </div>

            <hr className="my-3" />

            {/* Nomination Form Fields */}
            <Form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-12 mb-3">
                        <Form.Label><strong>Award Type</strong><span className="required">*</span></Form.Label>
                        <RewardsListDropdown 
                            onAwardSelect={handleAwardSelect}
                            selectedAward={selectedAward} 
                            unavailableAwardIds={unavailableAwardIds}
                            awards={awardsWithEligibility}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12 mb-3">
                        <Form.Label><strong>Remarks</strong><span className="required">*</span></Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5} 
                            value={justification}
                            onChange={(e) => setJustification(e.target.value)}
                            placeholder="Please provide your remarks about the nomination."
                        />
                    </div>
                </div> 
                
                {/* Call-to-Action Buttons */}
                <div className="d-flex justify-content-end mt-4">
                    <button
                        type="button"
                        className="cancel-btn buttonsize me-2"
                        onClick={onHide}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <Button
                        type="submit" 
                        title={isSubmitting ? "Submitting..." : "Submit Nomination"}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedAward || !justification.trim()}
                        className="add-submit-button buttonsize"
                    />
                </div>
            </Form>
        </div>
    );

    return (
        <ModalComponents
            show={showModal}
            onHide={onHide}
            title="Nomination Request"
            modalBody={modalBodyContent}
            className="nomination-modal"
        />  
    );
}